import { trackEvent } from '../utils/analytics.js';

/**
 * Service to handle analytics tracking for candidate search and related activities
 * This centralizes all analytics tracking logic in one place
 */
const candidateAnalyticsService = {
  /**
   * Track page view event
   * @param {string} pageName - Name of the page being viewed (optional)
   */
  trackPageView: (pageName = 'candidate_search') => {
    trackEvent('page_view', {
      page_name: pageName,
      timestamp: new Date().toISOString()
    });
  },
  
  /**
   * Track page exit event
   * @param {string} pageName - Name of the page being exited (optional)
   */
  trackPageExit: (pageName = 'candidate_search') => {
    trackEvent('page_exit', {
      page_name: pageName,
      time_spent: window.performance && window.performance.now ? 
        Math.round(window.performance.now() / 1000) : null,
      timestamp: new Date().toISOString()
    });
  },

  /**
   * Track a search query and its filters
   * @param {Object} searchData - Search data
   * @param {string} searchData.query - Search query
   * @param {Object} searchData.filters - Applied filters
   */
  trackSearch: (searchData) => {
    const { query = '', filters = {} } = searchData;
    
    trackEvent('candidate_search', {
      query: query || 'empty',
      filters: {
        skills: Array.isArray(filters.skills) ? filters.skills.length : 0,
        experience_level: filters.experience_level || 'any',
        hourly_rate_range: filters.hourly_rate_min && filters.hourly_rate_max 
          ? `${filters.hourly_rate_min}-${filters.hourly_rate_max}` 
          : 'any',
        location: filters.location || 'any',
        availability: filters.availability || 'any',
        rating_min: filters.rating_min || 'any'
      },
      results_count: searchData.resultsCount || 0
    });
  },
  
  /**
   * Track when a user views a candidate profile
   * @param {string} candidateId - ID of the candidate
   * @param {string} source - Source of the view (search, saved, etc.)
   */
  trackProfileView: (candidateId, source = 'search') => {
    if (!candidateId) return;
    
    trackEvent('view_candidate_profile', {
      candidate_id: candidateId,
      source
    });
  },
  
  /**
   * Track when a user saves/unsaves a candidate
   * @param {string} candidateId - ID of the candidate
   * @param {boolean} isSaving - Whether the user is saving (true) or unsaving (false)
   */
  trackSaveCandidate: (candidateId, isSaving) => {
    if (!candidateId) return;
    
    trackEvent(isSaving ? 'save_candidate' : 'unsave_candidate', {
      candidate_id: candidateId
    });
  },
  
  /**
   * Track when a user contacts a candidate
   * @param {string} candidateId - ID of the candidate
   * @param {string} employerId - ID of the employer
   */
  trackContactCandidate: (candidateId, employerId) => {
    if (!candidateId || !employerId) return;
    
    trackEvent('contact_candidate', {
      candidate_id: candidateId,
      employer_id: employerId
    });
  },
  
  /**
   * Track when a user applies a filter
   * @param {string} filterType - Type of filter (skills, experience, etc.)
   * @param {any} filterValue - Value of the filter
   */
  trackFilterUse: (filterType, filterValue) => {
    trackEvent('apply_candidate_filter', {
      filter_type: filterType,
      filter_value: filterValue
    });
  },
  
  /**
   * Track when a user changes the view mode
   * @param {string} viewMode - New view mode (grid, list)
   */
  trackViewModeChange: (viewMode) => {
    trackEvent('change_view_mode', {
      view_mode: viewMode
    });
  },
  
  /**
   * Track when a user loads more candidates
   * @param {number} page - Page number
   * @param {number} totalLoaded - Total candidates loaded so far
   */
  trackLoadMore: (page, totalLoaded) => {
    trackEvent('load_more_candidates', {
      page,
      total_loaded: totalLoaded
    });
  },
  
  /**
   * Track when a user clears all filters
   */
  trackClearFilters: () => {
    trackEvent('clear_all_filters', {
      timestamp: new Date().toISOString()
    });
  }
};

// Export both as named and default export for compatibility
export { candidateAnalyticsService };
export default candidateAnalyticsService;